import { logService } from 'npo-common';
import { httpRequest } from 'src/common/HttpRequest';
import authenticationService from 'src/dataServices/AuthenticationService';
import { supportTelemetryService } from 'src/dataServices/SupportTelemetryService';
import { ChatHistoryItem, FeedbackBootstrap, FeedbackSubmission } from 'src/state/contactus/types';

const API_BASE = process.env.REACT_APP_CS_API_BASEURL;

// Mirrors the customer support API contract (SupportTicketEventOutcome). The customer
// support API deserializes the enum by name (Newtonsoft StringEnumConverter), so these
// values are sent verbatim as the Outcome field.
export type SupportTicketEventOutcome = 'Submitted' | 'Cancelled';

export interface ISupportTicketEvent {
  // The AG-UI conversation thread id. Used by the API as the Cosmos partition key so the
  // event is stored with the rest of the conversation. Logging is skipped when absent.
  threadId?: string;
  outcome: SupportTicketEventOutcome;
  // The ticket category/topic. Recorded when the ticket is submitted; ignored on cancel.
  category?: string;
  // The user's selected role on the support ticket. Recorded when the ticket is
  // submitted; ignored on cancel.
  role?: string;
}

export const feedbackService = {
  async getReasonCodes(
    responseId: string,
    polarity: 'positive' | 'negative',
    threadId?: string,
    chatHistory?: ChatHistoryItem[]
  ): Promise<FeedbackBootstrap> {
    const response = await fetch(`${API_BASE}/feedback/reason-codes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        responseId,
        polarity,
        threadId,
        chatHistory: chatHistory || []
      })
    });

    if (!response.ok) {
      throw new Error(`Reason codes request failed: ${response.status}`);
    }

    return response.json();
  },

  async submitFeedback(submission: FeedbackSubmission): Promise<void> {
    const response = await fetch(`${API_BASE}/feedback/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(submission)
    });

    if (!response.ok) {
      throw new Error(`Feedback submission failed: ${response.status}`);
    }

    supportTelemetryService.trackFeedbackSubmitted(submission);
  },

  /**
   * Records the outcome of a Contact Us support ticket proposal (submitted or cancelled)
   * in the customer support API so it is captured in the Cosmos conversation history
   * alongside the chat messages for the same thread.
   *
   * Unlike the feedback calls above, this is best-effort and fire-and-forget: a logging
   * failure never throws to the caller or blocks the ticket flow, since the user-facing
   * outcome has already been resolved.
   */
  async logSupportTicketEvent(event: ISupportTicketEvent): Promise<void> {
    if (!event.threadId) {
      return;
    }

    try {
      const payload: { threadId: string; outcome: SupportTicketEventOutcome; category?: string; role?: string } = {
        threadId: event.threadId,
        outcome: event.outcome
      };

      if (event.outcome === 'Submitted') {
        if (event.category) {
          payload.category = event.category;
        }
        if (event.role) {
          payload.role = event.role;
        }
      }

      const token = await authenticationService.getToken();
      const response = await httpRequest(
        `${API_BASE}/customersupport/logticket`,
        token,
        JSON.stringify(payload),
        'POST'
      );

      if (!response.ok) {
        logService.logError('Failed to log support ticket event.', {
          status: response.status,
          outcome: event.outcome
        });
      }
    } catch (error) {
      logService.logError('Failed to log support ticket event.', { error });
    }
  }
};
